<template>
  <div class="profile-container" :class="{ 'dark-mode': isDarkMode }">
    <div class="profile-content">

      <!-- Profile Header -->
      <div class="profile-header">
        <div class="profile-header-left">
          <div class="profile-avatar" @mouseenter="avatarHover = true" @mouseleave="avatarHover = false">
            {{ avatarHover ? '✨' : '👤' }}
          </div>
          <h1 class="profile-name">{{ user.firstName }} {{ user.lastName }}</h1>
          <p class="profile-email">{{ user.email }}</p>
        </div>
        <div v-if="user.qr_code" class="profile-header-right">
          <QRCode
            :code="user.qr_code"
            title="My Loyalty QR"
            subtitle="Show to cashier to earn points"
            instructions="Present at the counter when ordering"
            size="medium"
            :showCode="false"
          />
        </div>
      </div>

      <!-- Profile Main Content -->
      <div class="profile-main">

        <!-- Loyalty Points Section -->
        <div class="loyalty-points-section">
          <div class="points-header">
            <h2 class="section-title">⭐ Loyalty Points</h2>

            <div class="points-balance">
              <div class="points-circle">
                <div class="points-value">{{ user.loyalty_points || 0 }}</div>
                <div class="points-unit">points</div>
              </div>

              <div class="points-value-display">
                <div class="points-peso-value">₱{{ ((user.loyalty_points || 0) / 4).toFixed(2) }}</div>
                <div class="points-value-label">in value</div>
              </div>
            </div>
          </div>

          <div class="points-actions">
            <button class="promotions-btn" @click="$emit('setCurrentPage', 'Promotions')">
              🎁 Browse Promotions
            </button>
            <button
              class="redeem-btn"
              :disabled="!canRedeem"
              @click="showRedeemPanel = !showRedeemPanel"
            >
              🎟️ Redeem Points
            </button>
          </div>

          <div v-if="showRedeemPanel" class="redeem-panel">
            <div class="redeem-header">Use Points at Checkout</div>
            <div class="redeem-slider-row">
              <input
                type="range"
                v-model.number="pointsToRedeem"
                :min="40"
                :max="maxRedeemPoints"
                step="4"
                class="redeem-slider"
              />
              <span class="redeem-points-label">{{ pointsToRedeem }} pts</span>
            </div>
            <div class="redeem-discount-preview">= ₱{{ redeemDiscount }} discount at checkout</div>
            <div class="redeem-actions">
              <button class="redeem-confirm-btn" @click="redeemLoyaltyPoints">
                Apply at Checkout
              </button>
              <button class="redeem-cancel-btn" @click="showRedeemPanel = false">Cancel</button>
            </div>
          </div>

          <div class="points-info">
            <div class="info-item">
              <span class="info-label">Earn Rate:</span>
              <span class="info-value">20% of order value</span>
            </div>
            <div class="info-item">
              <span class="info-label">Redemption:</span>
              <span class="info-value">4 points = ₱1 discount</span>
            </div>
            <div class="info-item">
              <span class="info-label">Min Redemption:</span>
              <span class="info-value">40 points (₱10)</span>
            </div>
          </div>
        </div>

        <!-- Vouchers Section -->
        <div class="vouchers-section">
          <div class="section-header">
            <h2 class="section-title">{{ user.vouchers.length > 0 ? 'My Vouchers' : 'No Vouchers Yet' }}</h2>

            <a v-if="user.vouchers.length > 2" href="#" class="see-all-btn" @click.prevent="showAllVouchers">
              {{ showAllVouchersFlag ? 'Show Less' : 'See All' }}
            </a>
          </div>

          <div v-if="user.vouchers.length > 0" class="vouchers-grid">
            <div 
              v-for="voucher in displayedVouchers" 
              :key="voucher.id"
              class="voucher-card"
              @click="openVoucherModal(voucher)"
            >
              <div class="voucher-icon">
                {{ getVoucherIcon(voucher.title) }}
              </div>
              <h3 class="voucher-title">{{ voucher.title }}</h3>
              <p class="voucher-subtitle">{{ voucher.subtitle }}</p>
              <span class="voucher-discount">{{ voucher.discount }}</span>
            </div>
          </div>

          <div v-else class="empty-vouchers">
            <div class="empty-icon">🎫</div>
            <p class="empty-text">Save vouchers from promotions to use them later!</p>
            <button class="promotions-btn" @click="$emit('setCurrentPage', 'Promotions')">
              Browse Promotions
            </button>
          </div>
        </div>

        <!-- Settings Section -->
       <!-- <div class="settings-section">
          <h3 class="settings-title">Account & App Settings</h3>
          <p class="settings-description">Manage your profile information and app preferences</p>

          <div class="settings-buttons">
            <button class="settings-btn profile-settings-btn" @click="$emit('setCurrentPage', 'ProfileSettings')">
              👤 Profile Settings
            </button>
            <button class="settings-btn app-settings-btn" @click="$emit('setCurrentPage', 'Settings')">
              ⚙️ App Settings
            </button>
          </div>
        </div>-->

        <div class="settings-section">
          <h3 class="settings-title">Orders & Payments</h3>
          <p class="settings-description">View your order history and payment transactions</p>

          <div class="settings-buttons">
            <button class="settings-btn order-history-btn" @click="$emit('setCurrentPage', 'OrderHistory')">
              📦 Order History
            </button>
            <button class="settings-btn payment-history-btn" @click="$emit('setCurrentPage', 'PaymentHistory')">
              💳 Payment History
            </button>
          </div>
        </div>

      </div>
    </div>

    <VoucherModal 
      :voucher="selectedVoucher"
      :isVisible="showVoucherModal"
      @close="closeVoucherModal"
      @useVoucher="useVoucher" 
      @saveVoucher="handleSaveVoucher"
      @removeVoucher="handleRemoveVoucher"
    />
  </div>
</template>

<script>
import VoucherModal from './VoucherModal.vue'
import QRCode from './QRCode.vue'

export default {
  name: 'Profile',
  components: {
    VoucherModal,
    QRCode
  },
  emits: ['setCurrentPage'],
  data() {
    return {
      user: {
        firstName: '',
        lastName: '',
        email: '',
        points: 0,
        loyalty_points: 0,
        qr_code: null,
        vouchers: []
      },

      voucherBlacklist: [
        "PWD",
        "Senior Citizen"
      ],

      showVoucherModal: false,
      selectedVoucher: {},
      showAllVouchersFlag: false,
      isDarkMode: false,
      avatarHover: false,

      customerId: null,
      showRedeemPanel: false,
      pointsToRedeem: 40
    }
  },

  computed: {
    displayedVouchers() {
      return this.showAllVouchersFlag
        ? this.user.vouchers
        : this.user.vouchers.slice(0, 2)
    },
    canRedeem() {
      return (this.user.loyalty_points || 0) >= 40
    },
    maxRedeemPoints() {
      return Math.min(this.user.loyalty_points || 0, 80)
    },
    redeemDiscount() {
      return (this.pointsToRedeem / 4).toFixed(2)
    }
  },

  mounted() {
    this.loadUserData()
    this.loadDarkModePreference()
    this.fetchCurrentUser()
    this.loadSavedVouchers()
  },

  activated() {
    this.fetchCurrentUser()
  },

  watch: {
    'user.loyalty_points'() {
      this.$forceUpdate()
    },
    '$root.isDarkMode'(newVal) {
      this.isDarkMode = newVal
    }
  },

  methods: {

    // =====================================================
    // VOUCHER USE LOGIC — LOCALSTORAGE HANDOFF
    // =====================================================
    // In Profile.vue - Add this method
    useVoucher(voucher) {
      // Store the selected voucher for cart to use
      localStorage.setItem('ramyeon_selected_voucher', JSON.stringify(voucher))
      
      // Show success message
      this.showSuccessMessage("Voucher applied! Redirecting to cart...")
      
      // Remove from profile
      this.user.vouchers = this.user.vouchers.filter(v => v.id !== voucher.id)
      
      // Remove from saved vouchers
      const saved = JSON.parse(localStorage.getItem('ramyeon_saved_vouchers') || '[]')
      localStorage.setItem(
        'ramyeon_saved_vouchers',
        JSON.stringify(saved.filter(v => v.id !== voucher.id))
      )
      
      // Close modal and redirect to cart
      this.closeVoucherModal()
      
      // Navigate to cart after short delay
      setTimeout(() => {
        this.$emit('setCurrentPage', 'Cart')
      }, 1500)
    },

    // -----------------------------
    // LOYALTY REDEMPTION
    // -----------------------------
    redeemLoyaltyPoints() {
      localStorage.setItem('ramyeon_use_loyalty_points', JSON.stringify({
        points: this.pointsToRedeem,
        setAt: new Date().toISOString()
      }))
      this.showRedeemPanel = false
      this.showSuccessMessage(`${this.pointsToRedeem} points queued! Redirecting to cart...`)
      setTimeout(() => {
        this.$emit('setCurrentPage', 'Cart')
      }, 1200)
    },

    // -----------------------------
    // DARK MODE LOGIC
    // -----------------------------
    loadDarkModePreference() {
      const darkMode = localStorage.getItem('ramyeon_dark_mode')
      this.isDarkMode = darkMode === 'true'
    },

    // -----------------------------
    // USER SESSION / PROFILE LOGIC
    // -----------------------------
    loadUserData() {
      const userSession = localStorage.getItem('ramyeon_user_session')
      if (userSession) {
        const userData = JSON.parse(userSession)
        const firstName = userData.firstName || (userData.fullName ? userData.fullName.split(' ')[0] : '')
        const lastName = userData.lastName || (userData.fullName ? userData.fullName.split(' ').slice(1).join(' ') : '')
        this.user = {
          ...userData,
          firstName,
          lastName,
          vouchers: []
        }
      } else {
        this.user = {
          firstName: '',
          lastName: '',
          email: '',
          points: 0,
          loyalty_points: 0,
          qr_code: null,
          vouchers: []
        }
      }
    },

    // =====================================================
    // FILTER OUT BLACKLISTED VOUCHERS
    // =====================================================
    loadSavedVouchers() {
      const savedVouchers = JSON.parse(localStorage.getItem('ramyeon_saved_vouchers') || '[]')

      const filtered = savedVouchers.filter(voucher =>
        !this.voucherBlacklist.includes(voucher.title)
      )

      this.user.vouchers = filtered
    },

    async fetchCurrentUser() {
      try {
        const token = localStorage.getItem('access_token')
        if (!token) return

        const { authAPI } = await import('../services/api.js')
        const response = await authAPI.getProfile()

        let user = response.customer ? response.customer : response

        const first = user.first_name || user.firstName || (user.full_name?.split(' ')[0])
        const last = user.last_name || user.lastName || (user.full_name?.split(' ').slice(1).join(' ') || '')

        this.customerId = user.customer_id || user.id || null
        this.user = {
          ...this.user,
          firstName: first || this.user.firstName,
          lastName: last || this.user.lastName,
          email: user.email || this.user.email,
          loyalty_points: user.loyalty_points || 0,
          qr_code: user.qr_code || null
        }

        if (user.vouchers) {
          this.user.vouchers = user.vouchers.filter(v =>
            !this.voucherBlacklist.includes(v.title)
          )
        }

        // Sync fresh API data back to localStorage so loadUserData() stays accurate
        try {
          const stored = JSON.parse(localStorage.getItem('ramyeon_user_session') || '{}')
          localStorage.setItem('ramyeon_user_session', JSON.stringify({
            ...stored,
            firstName: this.user.firstName,
            lastName: this.user.lastName,
            fullName: `${this.user.firstName} ${this.user.lastName}`.trim(),
            email: this.user.email,
            loyalty_points: this.user.loyalty_points,
            qr_code: this.user.qr_code,
            id: this.customerId || stored.id,
          }))
        } catch { /* */ }

      } catch (e) {
        console.error(e)
      }
    },

    // -----------------------------
    // VOUCHER MODAL HANDLERS
    // -----------------------------
    openVoucherModal(voucher) {
      this.selectedVoucher = voucher
      this.showVoucherModal = true
    },

    closeVoucherModal() {
      this.showVoucherModal = false
      this.selectedVoucher = {}
    },

    handleUseVoucher(voucher) {
      this.showSuccessMessage("Voucher applied successfully!")

      this.user.vouchers = this.user.vouchers.filter(v => v.id !== voucher.id)

      const saved = JSON.parse(localStorage.getItem('ramyeon_saved_vouchers') || '[]')
      localStorage.setItem(
        'ramyeon_saved_vouchers',
        JSON.stringify(saved.filter(v => v.id !== voucher.id))
      )
    },

    handleSaveVoucher() {
      this.showSuccessMessage("Voucher saved for later!")
      this.loadSavedVouchers()
    },

    handleRemoveVoucher(voucher) {
      this.user.vouchers = this.user.vouchers.filter(v => v.id !== voucher.id)
      this.showSuccessMessage("Voucher removed!")
      this.closeVoucherModal()
    },

    showAllVouchers() {
      this.showAllVouchersFlag = !this.showAllVouchersFlag
    },

    // -----------------------------
    // FUTURISTIC NOTIFICATION SYSTEM
    // (Copied from Promotions.vue)
    // -----------------------------
    showSuccessMessage(message) {
      const notification = document.createElement('div')
      notification.innerHTML = `
        <div style="
          position: fixed;
          top: 20px;
          right: 20px;
          background: linear-gradient(135deg, #28a745, #20c997);
          color: white;
          padding: 15px 25px;
          border-radius: 12px;
          box-shadow: 0 8px 25px rgba(40, 167, 69, 0.3);
          z-index: 9999;
          font-family: 'Poppins', sans-serif;
          font-weight: 600;
          animation: slideInRight 0.3s ease-out;
          display: flex;
          align-items: center;
          gap: 10px;
        ">
          <span style="font-size: 1.2rem;">✅</span>
          ${message}
        </div>
      `
      document.body.appendChild(notification)

      setTimeout(() => {
        if (notification.parentNode) {
          notification.firstElementChild.style.animation = 'slideInRight 0.3s ease-in reverse'
          setTimeout(() => notification.remove(), 300)
        }
      }, 3000)
    },

    showErrorMessage(message) {
      const notification = document.createElement('div')
      notification.innerHTML = `
        <div style="
          position: fixed;
          top: 20px;
          right: 20px;
          background: linear-gradient(135deg, #dc3545, #c82333);
          color: white;
          padding: 15px 25px;
          border-radius: 12px;
          box-shadow: 0 8px 25px rgba(220, 53, 69, 0.3);
          z-index: 9999;
          font-family: 'Poppins', sans-serif;
          font-weight: 600;
          animation: slideInRight 0.3s ease-out;
          display: flex;
          align-items: center;
          gap: 10px;
        ">
          <span style="font-size: 1.2rem;">❌</span>
          ${message}
        </div>
      `
      document.body.appendChild(notification)

      setTimeout(() => {
        if (notification.parentNode) {
          notification.firstElementChild.style.animation = 'slideInRight 0.3s ease-in reverse'
          setTimeout(() => notification.remove(), 300)
        }
      }, 3000)
    },

    // -----------------------------
    // ICON HELPER
    // -----------------------------
    getVoucherIcon(title) {
      const icons = {
        'Shin Ramyun': '🍜',
        'Fish Cake': '🍢',
        'Welcome Bonus': '🎉',
        'Social Signup Bonus': '🎁',
        'Tteokbokki': '🌶️',
        'Kimchi': '🥬',
        default: '🎫'
      }
      return icons[title] || icons.default
    }
  }
}
</script>

<style src="./Profile.css" scoped></style>

<style scoped>
/* Additional futuristic animations */
@keyframes slideInRight {
  from {
    opacity: 0;
    transform: translateX(100px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes slideOutRight {
  from {
    opacity: 1;
    transform: translateX(0);
  }
  to {
    opacity: 0;
    transform: translateX(100px);
  }
}

/* Message notifications */
.message {
  padding: 18px 25px;
  border-radius: 15px;
  font-weight: 600;
  backdrop-filter: blur(20px);
  border: 1px solid;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
}

.message.success {
  background: linear-gradient(135deg, #28a745, #20c997);
  color: white;
  border-color: rgba(40, 167, 69, 0.3);
}

.message.error {
  background: linear-gradient(135deg, #dc3545, #c82333);
  color: white;
  border-color: rgba(220, 53, 69, 0.3);
}

.message.info {
  background: linear-gradient(135deg, #ff6f61, #ff8a80);
  color: white;
  border-color: rgba(255, 111, 97, 0.3);
}

/* Loyalty Points Section */
.loyalty-points-section {
  background: linear-gradient(135deg, #fff8e1, #ffecb3);
  border-radius: 20px;
  padding: 25px;
  margin-bottom: 30px;
  border: 2px solid #ffb74d;
  box-shadow: 0 8px 25px rgba(255, 152, 0, 0.15);
}

.points-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.points-balance {
  display: flex;
  align-items: center;
  gap: 20px;
}

.points-circle {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #ff9800, #f57c00);
  border-radius: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
  box-shadow: 0 4px 15px rgba(255, 152, 0, 0.3);
}

.points-value {
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1;
}

.points-unit {
  font-size: 0.8rem;
  font-weight: 500;
  opacity: 0.9;
}

.points-value-display {
  text-align: center;
}

.points-peso-value {
  font-size: 1.8rem;
  font-weight: 700;
  color: #e65100;
  line-height: 1;
}

.points-value-label {
  font-size: 0.9rem;
  color: #bf360c;
  font-weight: 500;
}

.points-actions {
  display: flex;
  gap: 15px;
  margin-bottom: 20px;
}

.promotions-btn,
.points-history-btn {
  flex: 1;
  padding: 12px 20px;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.95rem;
}

.promotions-btn {
  background: linear-gradient(135deg, #ff4757, #ff3742);
  color: white;
}

.promotions-btn:hover {
  background: linear-gradient(135deg, #ff3742, #ff2f3a);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(255, 71, 87, 0.3);
}

.points-history-btn {
  background: linear-gradient(135deg, #ff9800, #f57c00);
  color: white;
}

.points-history-btn:hover {
  background: linear-gradient(135deg, #f57c00, #ef6c00);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(255, 152, 0, 0.3);
}

.points-info {
  background: rgba(255, 255, 255, 0.7);
  border-radius: 12px;
  padding: 15px;
  border: 1px solid rgba(255, 152, 0, 0.2);
}

.info-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid rgba(255, 152, 0, 0.1);
}

.info-item:last-child {
  border-bottom: none;
}

.info-label {
  font-weight: 600;
  color: #e65100;
  font-size: 0.9rem;
}

.info-value {
  font-weight: 500;
  color: #bf360c;
  font-size: 0.9rem;
}

.redeem-btn {
  flex: 1;
  padding: 12px 20px;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.95rem;
  background: linear-gradient(135deg, #ff9800, #f57c00);
  color: white;
}

.redeem-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #f57c00, #ef6c00);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(255, 152, 0, 0.3);
}

.redeem-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.redeem-panel {
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(255, 152, 0, 0.3);
  border-radius: 14px;
  padding: 18px;
  margin-top: 16px;
}

.redeem-header {
  font-weight: 700;
  font-size: 1rem;
  color: #e65100;
  margin-bottom: 14px;
}

.redeem-slider-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;
}

.redeem-slider {
  flex: 1;
  accent-color: #ff9800;
}

.redeem-points-label {
  font-weight: 700;
  color: #e65100;
  min-width: 60px;
  text-align: right;
}

.redeem-discount-preview {
  font-size: 1.1rem;
  font-weight: 600;
  color: #388e3c;
  margin-bottom: 16px;
  text-align: center;
}

.redeem-actions {
  display: flex;
  gap: 10px;
}

.redeem-confirm-btn {
  flex: 2;
  padding: 10px;
  background: linear-gradient(135deg, #28a745, #20c997);
  color: white;
  border: none;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.redeem-confirm-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.redeem-cancel-btn {
  flex: 1;
  padding: 10px;
  background: rgba(0, 0, 0, 0.08);
  color: #555;
  border: none;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.redeem-cancel-btn:hover {
  background: rgba(0, 0, 0, 0.14);
}

/* Smooth hover effect */
.promotions-btn {
  transition: all 0.3s ease;
}

/* Responsive enhancements */
@media (max-width: 480px) {
  .voucher-icon {
    width: 60px;
    height: 60px;
    font-size: 1.8rem;
  }
  
  .voucher-title {
    font-size: 1.2rem;
  }
  
  .voucher-subtitle {
    font-size: 0.85rem;
  }
  
  .voucher-discount {
    font-size: 0.9rem;
    padding: 8px 16px;
  }
  
  .empty-icon {
    font-size: 4rem;
  }
}
</style>
